import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Skeleton } from 'antd';
import { PaperClipOutlined, ReadOutlined, SafetyCertificateOutlined, SearchOutlined, ThunderboltOutlined } from '@ant-design/icons';

import { authApi } from '../lib/authApi';
import { wikiApi, type WikiArticleListItem } from '../lib/wikiApi';
import { kbShortDate } from '../lib/api/kb';
import { MATERIAL_LABELS, TYPE_CHIPS, materialIcon } from '../lib/sectionIcons';
import { Button, Chip, fmtInt, plural } from '../components/ds';
import './KnowledgeBase.css';

/** Результат в группе: статья / стандарт / файл / курс. */
interface ResultRow {
  key: string;
  to: string;
  title: string;
  sub: string | null;
  meta: string;
  icon: JSX.Element;
  tone: 'orange' | 'indigo' | 'blue' | 'green';
  articleId?: string;
}

const TONES: Record<ResultRow['tone'], [string, string]> = {
  orange: ['var(--fk-orange-soft)', 'var(--fk-orange)'],
  indigo: ['rgba(37,29,74,.08)', 'var(--fk-indigo)'],
  blue: ['var(--fk-blue-soft)', 'var(--fk-blue)'],
  green: ['var(--fk-success-soft)', 'var(--fk-success-text)'],
};

/**
 * Страница результатов поиска (HUB 2.0, прототип «search»). ?q= из URL →
 * /api/wiki/articles?search=… (FTS russian + синонимы + триграммы + гибрид по
 * смыслу). Группы: Статьи / Стандарты (requiresAck) / Файлы / Курсы (файлы и
 * курсы — из grouped-search). Фильтры в URL: type, block, mine (для моей
 * роли), verified, from, to. Ноль результатов → «Спросить AI» / «Задать вопрос УК»
 * (запрос уже записан в «Искали — не нашли»).
 */
export function SearchPage(): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const type = params.get('type') ?? '';
  const block = params.get('block') ?? '';
  const mine = params.get('mine') === '1';
  const verified = params.get('verified') === '1';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  // Поле поиска — локальное, в URL уходит по Enter.
  const [input, setInput] = useState(q);
  useEffect(() => setInput(q), [q]);

  const meQuery = useQuery({ queryKey: ['wiki-auth', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 });
  const blocksQuery = useQuery({ queryKey: ['wiki', 'blocks'], queryFn: () => wikiApi.listBlocks(), staleTime: 300_000 });
  const roleCode = meQuery.data?.roleCode ?? null;
  const blockNameByCode = useMemo(() => {
    const m: Record<string, string> = {};
    for (const b of blocksQuery.data ?? []) m[b.code] = b.name;
    return m;
  }, [blocksQuery.data]);

  const setParam = (k: string, v: string): void => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const submit = (): void => {
    const v = input.trim();
    const next = new URLSearchParams(params);
    if (v) next.set('q', v);
    else next.delete('q');
    setParams(next);
  };

  const enabled = q.trim().length >= 2;
  const { data, isLoading, error } = useQuery({
    queryKey: ['wiki', 'search', q, type, block, mine ? roleCode : '', verified, from, to],
    queryFn: () =>
      wikiApi.listArticles({
        search: q,
        limit: 50,
        type: type || undefined,
        blockCode: block || undefined,
        audienceRole: mine && roleCode ? roleCode : undefined,
        verifiedOnly: verified,
        publishedFrom: from || undefined,
        publishedTo: to || undefined,
        hybrid: true,
      }),
    enabled,
  });
  // Файлы и курсы — из сгруппированного поиска (те же ACL на бэке).
  const extra = useQuery({
    queryKey: ['wiki', 'grouped', q],
    queryFn: () => wikiApi.searchGrouped(q),
    enabled: enabled && !type && !block && !verified && !mine,
    staleTime: 30_000,
  });

  const groups = useMemo(() => {
    const items = data?.items ?? [];
    const toRow = (a: WikiArticleListItem, std: boolean): ResultRow => ({
      key: a.id,
      to: `/article/${a.slug}`,
      title: `${a.icon ? `${a.icon} ` : ''}${a.title}`,
      sub: a.snippet ?? a.excerpt ?? null,
      meta: [a.categoryName || (a.blocks[0] ? blockNameByCode[a.blocks[0]] : ''), kbShortDate(a.publishedAt)].filter(Boolean).join(' · '),
      icon: std ? <SafetyCertificateOutlined /> : <>{materialIcon(a.type)}</>,
      tone: std ? 'indigo' : 'orange',
      articleId: a.id,
    });
    const out: Array<{ title: string; rows: ResultRow[] }> = [];
    const arts = items.filter((a) => !a.requiresAck).map((a) => toRow(a, false));
    const stds = items.filter((a) => a.requiresAck).map((a) => toRow(a, true));
    if (arts.length) out.push({ title: 'Статьи', rows: arts });
    if (stds.length) out.push({ title: 'Стандарты', rows: stds });
    const files = (extra.data?.files ?? []).map<ResultRow>((f) => ({
      key: `f:${f.id}`,
      to: f.articleSlug ? `/article/${f.articleSlug}` : '/disk',
      title: f.fileName,
      sub: f.articleTitle ? `в статье «${f.articleTitle}»` : null,
      meta: f.kind,
      icon: <PaperClipOutlined />,
      tone: 'blue',
    }));
    const courses = (extra.data?.courses ?? []).map<ResultRow>((c) => ({
      key: `c:${c.id}`,
      to: `/course/${c.id}`,
      title: c.name,
      sub: c.description,
      meta: 'Курс',
      icon: <ReadOutlined />,
      tone: 'green',
    }));
    if (files.length) out.push({ title: 'Файлы', rows: files });
    if (courses.length) out.push({ title: 'Курсы', rows: courses });
    return out;
  }, [data, extra.data, blockNameByCode]);

  const hasFilters = !!(type || block || mine || verified || from || to);
  const total = (data?.total ?? 0) + (extra.data ? extra.data.files.length + extra.data.courses.length : 0);
  const noResults = enabled && !isLoading && !error && groups.length === 0;

  return (
    <div className="fk-kb-stack" style={{ gap: 18, maxWidth: 1100 }}>
      <form
        className="fk-kb-search-big"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <SearchOutlined />
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Поиск по базе знаний" aria-label="Поиск по базе знаний" autoFocus={!q} />
      </form>

      <div className="fk-kb-chips">
        {TYPE_CHIPS.map((c) => (
          <Chip key={c.label} active={(c.type ?? '') === type} onClick={() => setParam('type', c.type ?? '')}>
            {c.label}
          </Chip>
        ))}
        <span className="fk-kb-sep" />
        <select className="fk-kb-select" value={block} onChange={(e) => setParam('block', e.target.value)} aria-label="Раздел">
          <option value="">Все разделы</option>
          {(blocksQuery.data ?? []).map((b) => (
            <option key={b.code} value={b.code}>
              {b.name}
            </option>
          ))}
        </select>
        <label className="fk-kb-check">
          <input type="checkbox" checked={verified} onChange={(e) => setParam('verified', e.target.checked ? '1' : '')} />
          Только проверенные
        </label>
        {roleCode && (
          <label className="fk-kb-check">
            <input type="checkbox" checked={mine} onChange={(e) => setParam('mine', e.target.checked ? '1' : '')} />
            Для моей роли
          </label>
        )}
        <input type="date" className="fk-kb-select" value={from} onChange={(e) => setParam('from', e.target.value)} title="Опубликовано с" aria-label="Опубликовано с" />
        <input type="date" className="fk-kb-select" value={to} onChange={(e) => setParam('to', e.target.value)} title="Опубликовано по" aria-label="Опубликовано по" />
        {hasFilters && (
          <button type="button" className="fk-kb-check" style={{ background: 'none', border: 'none', color: 'var(--fk-orange)', fontWeight: 600 }} onClick={() => setParams(new URLSearchParams({ q }), { replace: true })}>
            Сбросить
          </button>
        )}
        <span style={{ flex: 1 }} />
        {enabled && data && (
          <span className="fk-kb-muted" style={{ fontSize: 13 }}>
            {fmtInt(total)} {plural(total, 'результат', 'результата', 'результатов')}
            {data.expandedWith && data.expandedWith.length > 0 && <> · также: {data.expandedWith.join(', ')}</>}
            {data.hybrid && <> · по смыслу</>}
          </span>
        )}
      </div>

      {!enabled ? (
        <div className="fk-kb-muted" style={{ fontSize: 14 }}>
          Введите минимум 2 символа — ищем по заголовкам, тексту и файлам с учётом морфологии и синонимов.
        </div>
      ) : isLoading ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : error ? (
        <Alert type="error" message="Поиск не сработал" description={(error as Error).message} showIcon />
      ) : noResults ? (
        <div className="fk-kb-panel fk-kb-empty">
          <img src="/brand/illustrations/onigiri-mono.png" alt="" style={{ height: 64, opacity: 0.6 }} />
          <div style={{ fontWeight: 700, marginTop: 10 }}>
            По запросу «{q}» ничего не нашли{hasFilters ? ' с такими фильтрами' : ''}
          </div>
          {data?.didYouMean ? (
            <div style={{ fontSize: 14, marginTop: 4 }}>
              Возможно, вы имели в виду: <Link to={`/search?q=${encodeURIComponent(data.didYouMean)}`}>{data.didYouMean}</Link>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--fk-text-2)', marginTop: 4 }}>Запрос записан в «Искали — не нашли» — УК увидит пробел. А пока:</div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 14, flexWrap: 'wrap' }}>
            <Button variant="primary" icon={<ThunderboltOutlined />} onClick={() => navigate(`/ai?q=${encodeURIComponent(q)}`)}>
              Спросить AI
            </Button>
            <Button variant="ghost" to={`/knowledge/ask?q=${encodeURIComponent(q)}`}>
              Задать вопрос УК
            </Button>
          </div>
        </div>
      ) : (
        groups.map((g) => (
          <section key={g.title}>
            <div className="fk-kb-cap">
              {g.title} · {g.rows.length}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {g.rows.map((r) => (
                <Link
                  key={r.key}
                  to={r.to}
                  className="fk-kb-result"
                  onClick={() => {
                    if (r.articleId) void wikiApi.searchClick(q, r.articleId);
                  }}
                >
                  <span className="fk-kb-tile" style={{ background: TONES[r.tone][0], color: TONES[r.tone][1] }}>
                    {r.icon}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>{r.title}</div>
                    {r.sub && <div style={{ fontSize: 13, color: 'var(--fk-text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.sub}</div>}
                  </div>
                  <span className="fk-kb-result-meta" style={{ fontSize: 12, color: 'var(--fk-text-muted)', textAlign: 'right' }}>
                    {r.meta || MATERIAL_LABELS.article}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
